import React, { useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import { buildBracePayload } from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { AwardDropdown, PlacementGrid, SaveNote } from "./awardFields";
import { preferScores } from "./dogLabel";
import { seedBrace } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  breed: string;
  breedGroup: string;
  scores: IScore[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

/** UKC Brace: one 1st–4th list for the breed (pairs, either sex), then Best Brace. */
const BraceBreedCard: React.FC<IProps> = ({ breed, breedGroup, scores, event, show, onSaved }) => {
  const seeded = seedBrace(show, breed, breedGroup);

  const [placements, setPlacements] = useState<string[]>(seeded.placements);
  const [bestBrace, setBestBrace] = useState(seeded.bestBrace);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const formRef = useRef({ placements: seeded.placements, bestBrace: seeded.bestBrace });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const next = seedBrace(show, breed, breedGroup);
    setPlacements(next.placements);
    setBestBrace(next.bestBrace);
    formRef.current = { placements: next.placements, bestBrace: next.bestBrace };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      const current = formRef.current;
      try {
        await apiService.saveConformationNonLicensedBreedResults(
          event._id,
          show._id,
          buildBracePayload({
            breed,
            breedGroup,
            placements: current.placements,
            bestBrace: current.bestBrace,
          })
        );
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: "Failed to save Brace results.",
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const changePlacement = (index: number, id: string) => {
    setPlacements((prev) => {
      const next = [...prev];
      next[index] = id;
      formRef.current.placements = next;
      return next;
    });
    triggerAutoSave();
  };

  const changeBestBrace = (id: string) => {
    setBestBrace(id);
    formRef.current.bestBrace = id;
    triggerAutoSave();
  };

  return (
    <div className={styles.breedCard}>
      <div className={styles.breedCardHeader}>
        <span className={styles.breedName}>{breed}</span>
        <span className={styles.breedEntryCount}>
          {scores.length} brace{scores.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className={styles.classSection}>
        <div className={styles.classSectionTitle}>Brace</div>
        <PlacementGrid scores={scores} value={placements} onChange={changePlacement} />
      </div>
      <div className={styles.sectionDivider}>Best Brace</div>
      <div className={styles.awardGrid}>
        <AwardDropdown
          label="Best Brace"
          scores={preferScores(scores, [placements[0]])}
          value={bestBrace}
          onChange={changeBestBrace}
        />
      </div>
      <SaveNote status={saveStatus} />
    </div>
  );
};

export default BraceBreedCard;
